import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { PeriodPicker, periodoQS, periodoTexto } from '../components/PeriodPicker';
import { api } from '../lib/api';
import { useLive } from '../lib/useLive';
import { avisar, confirmar, textoDeError } from '../components/Dialogo';
import { usePeriodoInicial } from '../lib/preferencias';
import { usePermisos } from '../lib/permisos';

/**
 * ALERTAS (0.40.0; reemplaza a "Mi día"). Lo que dirección tiene que ver sin abrir los chats uno por uno:
 * quejas (DraIA y chats de Halory), chats esperando respuesta, citas que se cayeron y quien escribió sin
 * estar en la base.
 *
 * 0.41.0: "se ve como una vista interminable sin orden… que arriba esté algo para seleccionar cada sección,
 * que no sean listas infinitas (ni que cargue cosas que no está mostrando)". Arriba, un selector con las cinco
 * secciones y cuántas trae cada una; abajo, SÓLO la elegida, de 20 en 20. Cada petición trae los conteos y
 * la página que se ve (`GET alertas?seccion=&pagina=`); la sección va en la URL (`?sec=`), así que se puede
 * mandar la liga y el botón de atrás regresa a la anterior.
 */
type Sec = 'quejas' | 'esperando' | 'chats-queja' | 'citas' | 'sin-empatar' | 'registros';
interface Conv {
  id: string; nombre: string | null; patientId: string | null; canal: string; atiende: string | null;
  intent: string | null; resumen: string | null; esperando: boolean; horasEsperando: number | null;
  ultimoMensaje: string | null; actualizada: string; liga: string;
}
interface Queja { complaint_id: string; patient_id: string | null; nombre: string | null; fecha: string | null; texto: string | null; tratamiento: string | null; asesora: string | null; fuente: string | null; dias: number | null }
interface Caida { appointment_id: string; patient_id: string; nombre: string | null; fecha_hora: string; status: string; tratamiento: string | null; asesora: string | null; origen: string | null }
interface SinEmpatar { followup_id: string; canal: string; nota: string; created_at: string }
interface PersonaReg {
  id: string; nombre: string | null; alta: string; sucursal: string | null; asesora: string | null; origen: string | null; etapa: string | null;
  telefono: string | null; correo: string | null; citas: number; consultas: number; compras: number; halory: boolean; prueba: boolean; existe: boolean;
}
interface Registro { alerta_id: string; tipo: 'telefono_compartido' | 'mismo_nombre' | 'halory_ambiguo' | 'halory_ya_ligado'; origen: string | null; detalle: any; created_at: string; persona: PersonaReg | null; otros: PersonaReg[] }
interface Resp {
  periodo: { label: string; all: boolean };
  halory: { ok: boolean; motivo: string | null; queHacer: string | null; truncado: boolean };
  conteos: Record<Sec, number | null>;
  extra: { quejasResueltas: number; esperando24: number | null };
  seccion: Sec; filtro: '24' | null;
  pagina: number; paginas: number; por: number; total: number;
  items: any[];
}

const SECS: Sec[] = ['quejas', 'esperando', 'chats-queja', 'citas', 'sin-empatar', 'registros'];
const TITULO: Record<Sec, string> = {
  quejas: 'Quejas abiertas', esperando: 'Esperan respuesta', 'chats-queja': 'Chats de queja',
  citas: 'Citas que se cayeron', 'sin-empatar': 'Sin expediente', registros: 'Revisar registros',
};
// Por qué salió cada registro (hub_alertas_registro.tipo) y de dónde llegó.
const TIPO_REG: Record<string, string> = {
  telefono_compartido: 'Comparte teléfono con otro expediente', mismo_nombre: 'Mismo nombre que otro expediente, con otro teléfono',
  halory_ambiguo: 'Halory tiene varios contactos con su teléfono', halory_ya_ligado: 'En Halory ya era de otro expediente',
};
const ORIGEN_REG: Record<string, string> = { halory: 'llegó de Halory', draia: 'se registró en Dra. IA', draia_sitio: 'agendó en el sitio web', recepcion: 'se registró en recepción', invitacion: 'llegó por una invitación', loyalty: 'se registró en la app', dtb: 'expediente de DTB', cuentas_prueba: 'desde Cuentas de prueba', desconocida: 'se registró en Dra. IA, recepción o la app' };

const CSS = `
.al-tabs { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 8px; margin: 12px 0 16px; }
.al-reg { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr)); gap: 8px; }
.al-per { border: 1px solid var(--border); border-radius: 12px; padding: 9px 11px; display: grid; gap: 3px; min-width: 0; font-size: 12.5px; background: var(--bg-alt); }
.al-per.nuevo { background: var(--card); border-color: var(--gold); }
.al-per a { color: var(--gold-dark); font-weight: 700; font-size: 14px; overflow-wrap: anywhere; }
.al-per .al-meta { overflow-wrap: anywhere; }
.al-acciones { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-top: 4px; }
.al-acciones button { padding: 5px 12px; font-size: 12.5px; box-shadow: none; }
.al-tab { text-align: left; padding: 10px 12px 11px; border: 1px solid var(--border); border-radius: 14px; background: var(--card); color: var(--ink);
  cursor: pointer; min-width: 0; font: inherit; display: grid; gap: 1px; box-shadow: none; transition: border-color .15s, background .15s; }
.al-tab:hover { border-color: var(--gold); }
.al-tab[aria-selected="true"] { border-color: var(--gold); background: var(--gold-light); box-shadow: inset 0 0 0 1px var(--gold); }
.al-tab > span { font-size: 12.5px; font-weight: 600; color: var(--ink); }
.al-tab > strong { font-size: clamp(22px, 5vw, 26px); font-weight: 700; line-height: 1.15; }
.al-tab.hay > strong { color: var(--warning); }
.al-tab > small { font-size: 11px; color: var(--ink-soft); line-height: 1.3; }
@media (max-width: 759px) {
  .al-tabs { display: flex; overflow-x: auto; scrollbar-width: none; -webkit-overflow-scrolling: touch; margin-right: -16px; padding-right: 16px; padding-bottom: 2px; }
  .al-tabs::-webkit-scrollbar { display: none; }
  .al-tab { flex: 0 0 140px; }
}
.al-h2 { margin: 4px 2px 2px; font-size: 17px; font-weight: 600; color: var(--gold-dark); }
.al-nota { font-size: 12.5px; color: var(--ink-soft); margin: 0 2px 10px; line-height: 1.5; }
.al-filtros { display: flex; gap: 6px; flex-wrap: wrap; margin: 0 0 10px; }
.al-filtros button { padding: 5px 12px; font-size: 12.5px; box-shadow: none; }
.al-filtros button[aria-pressed="true"] { border-color: var(--gold); background: var(--gold-light); color: var(--gold-dark); font-weight: 700; }
.al-lista { display: grid; gap: 8px; transition: opacity .15s; }
.al-lista.cargando { opacity: .45; pointer-events: none; }
.al-item { padding: 11px 14px; display: grid; gap: 4px; min-width: 0; }
.al-fila { display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; justify-content: space-between; }
.al-fila b { font-size: 14.5px; }
.al-meta { font-size: 12px; color: var(--ink-soft); }
.al-texto { font-size: 13px; line-height: 1.45; overflow-wrap: anywhere; }
.al-pill { font-size: 11px; font-weight: 600; padding: 2px 9px; border-radius: 999px; background: var(--bg-alt); color: var(--ink-soft); white-space: nowrap; }
.al-pill.ambar { background: var(--warning-bg); color: var(--warning); }
.al-ligas { display: flex; gap: 12px; flex-wrap: wrap; font-size: 12.5px; align-items: center; }
.al-ligas a { color: var(--gold-dark); font-weight: 600; }
.al-pag { display: flex; gap: 10px; align-items: center; justify-content: center; flex-wrap: wrap; margin: 14px 0 0; font-size: 13px; color: var(--ink-soft); }
.al-pag button { padding: 6px 14px; font-size: 13px; box-shadow: none; }
.al-pag button:disabled { opacity: .4; cursor: default; }
@media (max-width: 480px) { .al-pag { flex-wrap: nowrap; gap: 8px; } .al-pag-pag { display: none; } .al-pag button { padding: 6px 11px; } }
`;

const fechaHora = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const dia = (iso: string | null) => (iso ? new Date(iso.length === 10 ? iso + 'T12:00:00' : iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : 'sin fecha');
const espera = (h: number | null) => (h == null ? '' : h < 1 ? 'hace menos de 1 h' : h < 48 ? `hace ${h} h` : `hace ${Math.round(h / 24)} días`);
const ESTADO: Record<string, string> = { cancelada: 'Cancelada', no_show: 'No llegó' };
const ORIGEN: Record<string, string> = { halory: 'Halory', external_api: 'Sitio web', internal: 'DraIA', crm_import: 'CRM histórico', loyalty_pos: 'recepción' };

function ConvItem({ c }: { c: Conv }) {
  return (
    <div className="card al-item" data-conv={c.id}>
      <div className="al-fila">
        <b>{c.nombre || 'Sin expediente en DTB'}</b>
        <span className="al-meta">{c.canal}{c.atiende ? ` · atiende ${c.atiende}` : ' · sin asignar'}</span>
      </div>
      {c.resumen && <div className="al-texto">{c.resumen}</div>}
      <div className="al-fila">
        <span className="al-meta">
          {c.esperando
            ? <span className={`al-pill ${(c.horasEsperando ?? 0) >= 24 ? 'ambar' : ''}`}>Escribió {espera(c.horasEsperando)}{(c.horasEsperando ?? 0) >= 24 ? ' · fuera de las 24 h de WhatsApp' : ''}</span>
            : <>Último movimiento {fechaHora(c.actualizada)}</>}
        </span>
        <span className="al-ligas">
          {c.patientId && <Link to={`/paciente/${c.patientId}`}>Ficha</Link>}
          <a href={c.liga} target="_blank" rel="noreferrer">Abrir en Halory</a>
        </span>
      </div>
    </div>
  );
}

export function Alertas() {
  const [sp, setSp] = useSearchParams();
  const pedida = sp.get('sec') as Sec | null;
  const sec: Sec | null = pedida && SECS.includes(pedida) ? pedida : null;
  const [per, setPer] = useState(usePeriodoInicial());
  const [pagina, setPagina] = useState(1);
  const [filtro, setFiltro] = useState<'' | '24'>('');
  const [d, setD] = useState<Resp | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const tiras = useRef<HTMLDivElement>(null);
  // Lo último que se pidió (periodo|sección|página|filtro): cuando el servidor elige la sección y la pantalla la
  // pone en la URL, no se vuelve a pedir lo mismo.
  const ultima = useRef('');
  const clave = (s: string | null) => `${per}|${s}|${pagina}|${s === 'esperando' ? filtro : ''}`;

  const load = (silencio = false) => {
    const qs = new URLSearchParams(periodoQS(per, 'todo'));
    if (sec) qs.set('seccion', sec);
    qs.set('pagina', String(pagina));
    if (sec === 'esperando' && filtro) qs.set('filtro', filtro);
    if (!silencio) setCargando(true);
    setError(null);
    ultima.current = clave(sec);
    return api<Resp>('alertas?' + qs.toString())
      .then((r) => {
        setD(r);
        ultima.current = `${per}|${r.seccion}|${r.pagina}|${r.seccion === 'esperando' ? filtro : ''}`;
        if (r.pagina !== pagina) setPagina(r.pagina);
        if (!sec) setSp((s) => { const n = new URLSearchParams(s); n.set('sec', r.seccion); return n; }, { replace: true });
      })
      .catch((e) => setError(textoDeError(e)))
      .finally(() => setCargando(false));
  };
  useEffect(() => { if (ultima.current !== clave(sec)) load(); }, [per, sec, pagina, filtro]);
  // En celular el selector se desliza de lado: la sección elegida se asoma sola (también al abrir una liga con ?sec=).
  useEffect(() => {
    const cont = tiras.current; const b = cont?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (b && cont && cont.scrollWidth > cont.clientWidth) cont.scrollTo({ left: Math.max(0, b.offsetLeft - 24), behavior: 'smooth' });
  }, [d?.seccion]);
  // Un mensaje nuevo de Halory, una cita o una queja nueva en DraIA: se repinta sola (sólo lo que se ve), sin parpadear.
  useLive(['followup', 'patient', 'inbound'], () => { load(true); });

  const elegir = (s: Sec) => {
    if (s === sec) return;
    setHecho(null);
    setPagina(1); setFiltro('');
    setSp((x) => { const n = new URLSearchParams(x); n.set('sec', s); return n; });
  };
  const irA = (p: number) => {
    setPagina(p);
    const top = panel.current?.getBoundingClientRect().top;
    if (top != null && top < 0) panel.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };
  const visto = (id: string) => {
    setD((x) => (x ? { ...x, items: x.items.filter((y) => y.followup_id !== id) } : x));
    api(`crm/sin-empatar/${id}/visto`, { method: 'POST', body: '{}' })
      .then(() => load(true))
      .catch((e) => { avisar({ titulo: 'No se pudo marcar', mensaje: textoDeError(e), tipo: 'error' }); load(true); });
  };

  const txt = periodoTexto(per).toLowerCase();
  const c = d?.conteos;
  const actual = d?.seccion || sec;
  const subtitulo = (s: Sec): string => {
    if (!d) return '';
    switch (s) {
      case 'quejas': return `DraIA · ${d.extra.quejasResueltas} cerradas en ${txt}`;
      case 'esperando': return d.extra.esperando24 != null ? `${d.extra.esperando24} con más de 24 h` : 'Halory, ahora';
      case 'chats-queja': return `Halory · ${txt}`;
      case 'citas': return `sin otra cita · ${txt}`;
      case 'registros': return 'posibles duplicados al registrarse';
      default: return 'escribieron y no están en la base';
    }
  };
  const desde = d ? (d.pagina - 1) * d.por + 1 : 0;
  const hasta = d ? Math.min(d.total, d.pagina * d.por) : 0;

  return (
    <div>
      <style>{CSS}</style>
      <TopBar />
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '18px 16px 48px' }}>
        <h1 style={{ margin: '4px 2px 4px', fontSize: 24, fontWeight: 300, color: 'var(--gold-dark)' }}>Alertas</h1>
        <p className="muted" style={{ marginTop: 0, fontSize: 14 }}>Lo que se está cayendo: quejas, chats sin respuesta y citas perdidas. Sale de Halory y de DraIA, sin abrir los chats uno por uno.</p>
        <PeriodPicker value={per} onChange={(v) => { setPagina(1); setPer(v); }} />

        {error && <div className="card" style={{ padding: 16, marginTop: 12, color: 'var(--warning)', background: 'var(--warning-bg)' }}>{error}</div>}
        {!d && !error && <div className="muted" style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>}

        {d && c && (<>
          {!d.halory.ok && (
            <div className="card" style={{ padding: '10px 14px', marginTop: 12, background: 'var(--warning-bg)', color: 'var(--warning)', fontSize: 13, border: 'none' }} data-halory-motivo>
              <b>No se pudo preguntar a Halory.</b> {d.halory.motivo} {d.halory.queHacer} Mientras tanto, los chats salen en «—»; las quejas de DraIA y las citas sí están al día.
            </div>
          )}

          <div ref={tiras} className="al-tabs" role="tablist" aria-label="Secciones de alertas" data-al="resumen">
            {SECS.map((s) => (
              <button key={s} type="button" role="tab" aria-selected={actual === s} aria-controls="al-panel" data-tab={s}
                className={`al-tab ${(c[s] || 0) > 0 ? 'hay' : ''}`} onClick={() => elegir(s)}>
                <span>{TITULO[s]}</span>
                <strong>{c[s] ?? '—'}</strong>
                <small>{subtitulo(s)}</small>
              </button>
            ))}
          </div>

          <div ref={panel} id="al-panel" role="tabpanel" data-al={d.seccion} style={{ scrollMarginTop: 70 }}>
            <h2 className="al-h2">{TITULO[d.seccion]}</h2>
            <p className="al-nota">
              {d.seccion === 'quejas' && <>Abiertas en DraIA: el equipo médico las registra y las cierra en la ficha de DraIA. No dependen del periodo; las más viejas primero.</>}
              {d.seccion === 'chats-queja' && <>Chats que la IA de Halory marcó como queja en {txt}, del más reciente al más viejo.</>}
              {d.seccion === 'esperando' && <>La persona escribió al último y nadie le ha contestado (Halory, en este momento). Los más viejos primero; pasadas 24 h, WhatsApp sólo deja escribirle con plantilla.</>}
              {d.seccion === 'citas' && <>Canceladas o a las que no llegó en {txt}, sin otra cita después: nadie las ha vuelto a agendar.</>}
              {d.seccion === 'registros' && <>Expedientes nuevos que pueden ser alguien que ya estaba: comparten teléfono con otro, traen el mismo nombre con otro teléfono, o Halory no supo con cuál ligarlo. Se crearon igual (nadie se queda sin atender) y no se juntan solos. «Son la misma persona» los junta como Salud de datos: se queda el que tiene más historia, y si los dos tienen historia clínica no se puede y se dice por qué. «Son personas distintas» la quita y no vuelve a salir. No depende del periodo.</>}
              {d.seccion === 'sin-empatar' && <>Llegaron por Halory y su teléfono no empata con ningún expediente (o empata con dos). Si Halory los da de alta como lead, pasan a la base solos. «Visto» los quita de aquí sin borrar que escribieron.</>}
            </p>
            {hecho && d.seccion === 'registros' && <div role="status" className="card" data-registro-hecho style={{ padding: '9px 14px', marginBottom: 10, fontSize: 13, background: 'var(--success-bg)', color: 'var(--success)', border: 'none' }}>{hecho}</div>}

            {d.seccion === 'esperando' && d.halory.ok && (
              <div className="al-filtros" role="group" aria-label="Filtro">
                <button type="button" className="btn-ghost" aria-pressed={!filtro} data-filtro="todos" onClick={() => { setPagina(1); setFiltro(''); }}>Todos ({c.esperando ?? 0})</button>
                <button type="button" className="btn-ghost" aria-pressed={filtro === '24'} data-filtro="24" onClick={() => { setPagina(1); setFiltro('24'); }}>Más de 24 h ({d.extra.esperando24 ?? 0})</button>
              </div>
            )}

            <div className={`al-lista ${cargando ? 'cargando' : ''}`} aria-busy={cargando}>
              {(d.seccion === 'esperando' || d.seccion === 'chats-queja') && !d.halory.ok && (
                <div className="card al-item"><span className="al-meta">No se pudo leer de Halory (arriba dice por qué).</span></div>
              )}
              {d.items.length === 0 && !((d.seccion === 'esperando' || d.seccion === 'chats-queja') && !d.halory.ok) && (
                <div className="card al-item" data-vacio><span className="al-meta">
                  {d.seccion === 'quejas' && 'Ninguna queja abierta en DraIA.'}
                  {d.seccion === 'chats-queja' && `Ningún chat de queja en ${txt}.`}
                  {d.seccion === 'esperando' && (filtro === '24' ? 'Ningún chat lleva más de 24 h esperando.' : 'Ningún chat esperando respuesta.')}
                  {d.seccion === 'citas' && `Ninguna cita perdida sin reagendar en ${txt}.`}
                  {d.seccion === 'sin-empatar' && 'Nadie pendiente: todos los que escribieron ya empataron o se marcaron como vistos.'}
                  {d.seccion === 'registros' && 'Nada que revisar: ningún registro nuevo se parece a otro expediente.'}
                </span></div>
              )}

              {d.seccion === 'quejas' && (d.items as Queja[]).map((q) => (
                <div key={q.complaint_id} className="card al-item" data-queja={q.complaint_id}>
                  <div className="al-fila">
                    <b>{q.nombre || 'Sin nombre'}</b>
                    <span className="al-pill ambar">Abierta {q.dias != null ? (q.dias <= 0 ? 'desde hoy' : `hace ${q.dias} día${q.dias === 1 ? '' : 's'}`) : ''}</span>
                  </div>
                  {q.texto && <div className="al-texto">{q.texto}</div>}
                  <div className="al-fila">
                    <span className="al-meta">{dia(q.fecha)}{q.tratamiento ? ` · ${q.tratamiento}` : ''}{q.asesora ? ` · atiende ${q.asesora}` : ''} · DraIA</span>
                    {q.patient_id && <span className="al-ligas"><Link to={`/paciente/${q.patient_id}`}>Ficha</Link></span>}
                  </div>
                </div>
              ))}
              {(d.seccion === 'esperando' || d.seccion === 'chats-queja') && (d.items as Conv[]).map((x) => <ConvItem key={x.id} c={x} />)}
              {d.seccion === 'citas' && (d.items as Caida[]).map((x) => (
                <div key={x.appointment_id} className="card al-item" data-cita={x.appointment_id}>
                  <div className="al-fila">
                    <b>{x.nombre || 'Sin nombre'}</b>
                    <span className="al-pill">{ESTADO[x.status] || x.status}</span>
                  </div>
                  <div className="al-fila">
                    <span className="al-meta">{fechaHora(x.fecha_hora)}{x.tratamiento ? ` · ${x.tratamiento}` : ''}{x.asesora ? ` · atiende ${x.asesora}` : ''}{x.origen ? ` · ${ORIGEN[x.origen] || x.origen}` : ''}</span>
                    <span className="al-ligas"><Link to={`/paciente/${x.patient_id}`}>Ficha</Link></span>
                  </div>
                </div>
              ))}
              {d.seccion === 'registros' && (d.items as Registro[]).map((x) => <RegistroItem key={x.alerta_id} r={x} onHecho={(t) => { setHecho(t); load(true); }} />)}
              {d.seccion === 'sin-empatar' && (d.items as SinEmpatar[]).map((x) => (
                <div key={x.followup_id} className="card al-item" data-sin-empatar={x.followup_id}>
                  <div className="al-fila">
                    <span className="al-pill">{x.canal}</span>
                    <span className="al-meta">{fechaHora(x.created_at)}</span>
                  </div>
                  <div className="al-fila" style={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                    <div className="al-texto" style={{ flex: 1, minWidth: 0 }}>{x.nota}</div>
                    <button type="button" className="btn-ghost" style={{ flexShrink: 0, padding: '4px 10px', fontSize: 12, boxShadow: 'none' }} data-visto onClick={() => visto(x.followup_id)}>Visto</button>
                  </div>
                </div>
              ))}
            </div>

            {d.total > d.por && (
              <nav className="al-pag" aria-label="Páginas">
                <button type="button" className="btn-ghost" data-pag-ant disabled={d.pagina <= 1 || cargando} onClick={() => irA(d.pagina - 1)}>Anterior</button>
                <span data-pag-txt>{desde}–{hasta} de {d.total}<span className="al-pag-pag"> · página {d.pagina} de {d.paginas}</span></span>
                <button type="button" className="btn-ghost" data-pag-sig disabled={d.pagina >= d.paginas || cargando} onClick={() => irA(d.pagina + 1)}>Siguiente</button>
              </nav>
            )}
          </div>
        </>)}
      </div>
    </div>
  );
}

/** Una persona de la alerta: lo justo para decidir si es la misma, sin teléfono ni correo completos. */
function TarjetaPersona({ p, nuevo }: { p: PersonaReg; nuevo?: boolean }) {
  const hist = [p.citas ? `${p.citas} cita${p.citas === 1 ? '' : 's'}` : null, p.consultas ? `${p.consultas} consulta${p.consultas === 1 ? '' : 's'} en Dra. IA` : null,
    p.compras ? `${p.compras} compra${p.compras === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ');
  return (
    <div className={`al-per ${nuevo ? 'nuevo' : ''}`} data-persona={p.id}>
      <span className="al-meta" style={{ fontWeight: 600 }}>{nuevo ? 'El registro nuevo' : 'El que ya estaba'}</span>
      <Link to={`/paciente/${p.id}`}>{p.nombre || 'Sin nombre'}</Link>
      <span className="al-meta">alta {dia(p.alta)}{p.sucursal ? ` · ${p.sucursal}` : ''}{p.etapa ? ` · ${p.etapa}` : ''}{p.asesora ? ` · ${p.asesora}` : ''}</span>
      {(p.telefono || p.correo) && <span className="al-meta">{[p.telefono, p.correo].filter(Boolean).join(' · ')}</span>}
      <span className="al-meta">{hist || 'sin historia todavía'}{p.halory ? ' · en Halory' : ''}{p.prueba ? ' · cuenta de prueba' : ''}{p.origen ? ` · llegó por ${p.origen}` : ''}</span>
    </div>
  );
}

function RegistroItem({ r, onHecho }: { r: Registro; onHecho: (t: string) => void }) {
  // Juntar dos expedientes no se deshace: lo hace sólo una cuenta de admin (el API contesta 403 a las demás).
  const { esAdmin } = usePermisos();
  const [ocupado, setOcupado] = useState(false);
  const p = r.persona;
  const juntar = async (otro: PersonaReg) => {
    if (!p) return;
    if (!(await confirmar({ titulo: '¿Son la misma persona?', peligro: true, confirmar: 'Juntar',
      mensaje: `${p.nombre || 'El registro nuevo'} y ${otro.nombre || 'el que ya estaba'} se vuelven UN expediente: se queda el que tiene más historia y el otro le pasa sus citas, compras y lo de Halory.\nSi los dos tienen historia clínica no se juntan (se dice por qué).` }))) return;
    setOcupado(true);
    try {
      await api(`alertas/registros/${r.alerta_id}`, { method: 'POST', body: JSON.stringify({ accion: 'juntar', con: otro.id }) });
      onHecho(`Listo: ${p.nombre || 'el registro'} y ${otro.nombre || 'el otro expediente'} ya son un solo expediente.`);
    } catch (e) { await avisar({ titulo: 'No se juntaron', mensaje: textoDeError(e), tipo: 'error' }); setOcupado(false); }
  };
  const descartar = async () => {
    if (!(await confirmar({ titulo: '¿Son personas distintas?', confirmar: 'Son personas distintas',
      mensaje: 'La alerta se quita y no vuelve a salir con estos mismos expedientes. Nada se junta ni se borra.' }))) return;
    setOcupado(true);
    try {
      await api(`alertas/registros/${r.alerta_id}`, { method: 'POST', body: JSON.stringify({ accion: 'descartar' }) });
      onHecho('Listo: quedan como personas distintas.');
    } catch (e) { await avisar({ titulo: 'No se pudo', mensaje: textoDeError(e), tipo: 'error' }); setOcupado(false); }
  };
  const extra = r.tipo === 'halory_ambiguo'
    ? (r.otros.length ? 'Sus contactos de Halory ya son de estos expedientes.' : 'Ninguno de esos contactos de Halory está ligado a un expediente: hay que ligarlo en Halory (o dejarlo así).')
    : r.tipo === 'telefono_compartido' && r.detalle?.telefono ? `Teléfono ${r.detalle.telefono}.` : null;
  return (
    <div className="card al-item" data-registro={r.alerta_id} data-tipo={r.tipo}>
      <div className="al-fila">
        <span className="al-pill ambar">{TIPO_REG[r.tipo] || r.tipo}</span>
        <span className="al-meta">{r.origen ? `${ORIGEN_REG[r.origen] || r.origen} · ` : ''}{fechaHora(r.created_at)}</span>
      </div>
      {extra && <div className="al-meta">{extra}</div>}
      <div className="al-reg">
        {p ? <TarjetaPersona p={p} nuevo /> : <div className="al-per"><span className="al-meta">El registro nuevo ya no existe.</span></div>}
        {r.otros.map((o) => <TarjetaPersona key={o.id} p={o} />)}
      </div>
      <div className="al-acciones">
        {esAdmin && p && r.otros.map((o) => (
          <button key={o.id} type="button" className="btn-ghost" disabled={ocupado} data-juntar={o.id} onClick={() => juntar(o)}>
            {r.otros.length === 1 ? 'Son la misma persona: juntar' : `Es ${o.nombre || 'el mismo'}: juntar`}</button>
        ))}
        <button type="button" className="btn-ghost" disabled={ocupado} data-descartar onClick={descartar}>{r.otros.length ? 'Son personas distintas' : 'Entendido: quitar'}</button>
      </div>
      {!esAdmin && p && r.otros.length > 0 && <div className="al-meta" data-solo-admin="juntar">Si son la misma persona, una cuenta de admin los junta.</div>}
    </div>
  );
}
